/**
 * «ДЕФЕКТЫ» — что нашёл сторож Айланы, пока никто не смотрел.
 *
 * ── ЗАЧЕМ ЭКРАН ─────────────────────────────────────────────────────────────
 * До 17.09.2026 дефекты Айланы находил ВЛАДЕЛЕЦ, случайно наткнувшись на плохой
 * ответ: так нашлись все девять дефектов сентябрьского разбора, включая поездку
 * не туда за 3,1 км. Ночная задача (`app/tasks/assistant.py`) теперь читает
 * вчерашние логи восемью детекторами — без вызовов модели — и складывает
 * подозрения сюда.
 *
 * ⚠️ СПИСОК, КОТОРЫЙ НИКТО НЕ ЧИТАЕТ, ХУЖЕ ОТСУТСТВУЮЩЕГО. Поэтому у каждой
 * строки есть «не дефект»: по доле таких отметок видно, какой детектор шумит и
 * какой порог пора ужесточать. Строку при этом НЕ удаляем — исчезнувшая
 * находка не расскажет, что детектор врёт.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Empty, Select, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import { PlayCircleOutlined, ReloadOutlined, UndoOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { L } from '../../styles/theme';
import { assistantApi, type DefectRow } from '../../api/assistant';

const { Text } = Typography;

/** Что ловит каждый детектор — человеческими словами, чтобы не лезть в код. */
const DETECTOR_LABEL: Record<string, string> = {
  taxi_name_lost: 'Такси: потеряно название места',
  promise_without_payload: 'Обещана кнопка или карточка, которой нет',
  facts_without_tool: 'Факты без вызова инструмента',
  duplicate_ask: 'Один вопрос — два вызова модели',
  session_broken: 'Разговор не собрался в цепочку',
  worse_option_picked: 'Предложен вариант хуже имеющегося',
  slow_or_retry: 'Долгий ответ, повтор или сбой',
  antirefusal_junk: 'Страховка искала место не в том вопросе',
};

const fmtDt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const cut = (s: string | null, n: number) => (!s ? '' : s.length > n ? `${s.slice(0, n)}…` : s);

export const DefectsTab: React.FC<{ onOpenLog: (id: number) => void }> = ({ onOpenLog }) => {
  const [rows, setRows] = useState<DefectRow[]>([]);
  const [counts, setCounts] = useState<{ detector: string; severity: string; count: number }[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detector, setDetector] = useState<string | undefined>();
  const [severity, setSeverity] = useState<'high' | 'normal' | undefined>();
  const [showDismissed, setShowDismissed] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await assistantApi.defects({
        detector, severity, show_dismissed: showDismissed,
        limit: pageSize, offset: (page - 1) * pageSize,
      });
      setRows(d.items);
      setCounts(d.by_detector);
      setTotal(d.total);
    } catch {
      message.error('Не удалось загрузить находки');
    } finally {
      setLoading(false);
    }
  }, [detector, severity, showDismissed, page]);

  useEffect(() => { void load(); }, [load]);

  const dismiss = async (row: DefectRow) => {
    const next = !row.dismissed_at;
    try {
      await assistantApi.dismissDefect(row.id, next);
      message.success(next ? 'Отмечено как «не дефект»' : 'Возвращено в список');
      void load();
    } catch {
      message.error('Не удалось изменить отметку');
    }
  };

  const scanNow = async () => {
    setBusy(true);
    try {
      const r = await assistantApi.scanDefects(1);
      message.success(`Прогон: строк ${r.scanned}, находок ${r.found}`);
      void load();
    } catch {
      message.error('Прогон не удался');
    } finally {
      setBusy(false);
    }
  };

  const columns: ColumnsType<DefectRow> = [
    {
      title: 'Когда', dataIndex: 'log_at', width: 130,
      render: (v: string) => <Text style={{ fontSize: 12.5 }}>{fmtDt(v)}</Text>,
    },
    {
      title: 'Что не так', dataIndex: 'detector', width: 260,
      render: (v: string, r) => (
        <Space direction="vertical" size={2}>
          <Space size={6}>
            <Tag color={r.severity === 'high' ? 'red' : 'default'} style={{ margin: 0 }}>
              {r.severity === 'high' ? 'важно' : 'обычное'}
            </Tag>
            {r.surface ? <Tag style={{ margin: 0 }}>{r.surface}</Tag> : null}
          </Space>
          <Text style={{ fontSize: 12.5 }}>{DETECTOR_LABEL[v] ?? v}</Text>
        </Space>
      ),
    },
    {
      title: 'Разбор', dataIndex: 'excerpt',
      render: (v: string | null, r) => (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Text style={{ fontSize: 12.5 }}>{v}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            #{r.log_id} · «{cut(r.message, 90)}»
          </Text>
        </Space>
      ),
    },
    {
      title: '', width: 140, align: 'right' as const,
      render: (_: unknown, r) => (
        <Tooltip title={r.dismissed_at
          ? 'Вернуть в список находок'
          : 'Это не дефект — убрать из списка (строка останется, по ней видно, какой детектор шумит)'}>
          <Button
            size="small"
            icon={r.dismissed_at ? <UndoOutlined /> : undefined}
            onClick={(e) => { e.stopPropagation(); void dismiss(r); }}
          >
            {r.dismissed_at ? 'Вернуть' : 'Не дефект'}
          </Button>
        </Tooltip>
      ),
    },
  ];

  const detectorOptions = counts
    .reduce<{ value: string; label: string }[]>((acc, c) => {
      const found = acc.find((x) => x.value === c.detector);
      if (found) return acc;
      const n = counts.filter((x) => x.detector === c.detector)
        .reduce((sum, x) => sum + x.count, 0);
      acc.push({ value: c.detector, label: `${DETECTOR_LABEL[c.detector] ?? c.detector} · ${n}` });
      return acc;
    }, []);

  return (
    <>
      <Card size="small" style={{ marginTop: 12 }} styles={{ body: { padding: 12 } }}>
        <Space wrap size={8}>
          <Select
            allowClear placeholder="Детектор" value={detector} style={{ width: 320 }}
            onChange={(v) => { setDetector(v); setPage(1); }}
            options={detectorOptions}
          />
          <Select
            allowClear placeholder="Важность" value={severity} style={{ width: 160 }}
            onChange={(v) => { setSeverity(v); setPage(1); }}
            options={[
              { value: 'high', label: 'важно' },
              { value: 'normal', label: 'обычное' },
            ]}
          />
          <Space size={6}>
            <Switch checked={showDismissed} onChange={(v) => { setShowDismissed(v); setPage(1); }} />
            <Text style={{ fontSize: 12.5, color: L.muted }}>показывать «не дефект»</Text>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={() => void load()}>Обновить</Button>
          <Tooltip title="Обычно сторож ходит ночью в 04:00. Кнопка прогоняет сегодняшние логи сейчас; повторный прогон дублей не создаёт.">
            <Button icon={<PlayCircleOutlined />} loading={busy} onClick={() => void scanNow()}>
              Прогнать сейчас
            </Button>
          </Tooltip>
        </Space>
      </Card>

      <Card size="small" style={{ marginTop: 12 }} styles={{ body: { padding: 0 } }}>
        <Table<DefectRow>
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          onRow={(r) => ({ onClick: () => onOpenLog(r.log_id), style: { cursor: 'pointer' } })}
          locale={{ emptyText: <Empty description="Находок под эти условия нет" /> }}
          pagination={{
            current: page, pageSize, total, showSizeChanger: false,
            showTotal: (t) => `всего ${t}`,
            onChange: (p) => setPage(p),
          }}
        />
      </Card>
    </>
  );
};

export default DefectsTab;
